import React from 'react';
type DecorProps = {
  className?: string;
};
type FlowerProps = DecorProps & {
  /** Text-color class for the petals, e.g. "text-rose-300". */
  petalClassName?: string;
  /** Text-color class for the flower center. */
  centerClassName?: string;
};

/** Botanical sprig with leaves — decorative. */
export function Sprig({
  className = ''
}: DecorProps) {
  return <svg viewBox="0 0 120 200" fill="none" aria-hidden="true" className={className} data-id="element-68" data-mp="awbu3fwwysz6">
      <path d="M60 196V16" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" data-id="element-69" data-mp="ayg66l4yuyaj" />
      {[0, 1, 2, 3, 4].map(index => {
      const y = 44 + index * 30;
      return <g key={index} data-id="element-70" data-mp="16a5mn0nf8jq">
            <path d={`M60 ${y} C40 ${y - 16} 20 ${y - 10} 12 ${y + 6} C30 ${y + 18} 50 ${y + 12} 60 ${y}Z`} fill="currentColor" opacity="0.35" data-id="element-71" data-mp="65n301p5by0k" />
            <path d={`M60 ${y + 14} C80 ${y - 2} 100 ${y + 4} 108 ${y + 20} C90 ${y + 32} 70 ${y + 26} 60 ${y + 14}Z`} fill="currentColor" opacity="0.22" data-id="element-72" data-mp="ylxrpyxn1052" />
          </g>;
    })}
      <circle cx="60" cy="16" r="7" fill="currentColor" opacity="0.5" data-id="element-73" data-mp="3b88es314gbzm" />
    </svg>;
}

/** Five-petal blossom (wild rose) — decorative. */
export function Blossom({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 100 100" fill="none" aria-hidden="true" className={className} data-id="element-74" data-mp="3k62772vrmlhi">
      <g className={petalClassName} data-id="element-75" data-mp="2thxqki6iba3">
        {[0, 1, 2, 3, 4].map(index => <ellipse key={index} cx="50" cy="26" rx="14" ry="22" fill="currentColor" opacity="0.55" transform={`rotate(${index * 72} 50 50)`} data-id="element-76" data-mp="3tx6ls1hj44bt" />)}
      </g>
      <circle cx="50" cy="50" r="9" fill="currentColor" opacity="0.85" className={centerClassName} data-id="element-77" data-mp="2din7tkw5maft" />
    </svg>;
}

/** Many-petal daisy / chamomile — decorative. */
export function Daisy({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 100 100" fill="none" aria-hidden="true" className={className} data-id="element-78" data-mp="hg1z3o6iqmkv">
      <g className={petalClassName} data-id="element-79" data-mp="mfel66pkfpme">
        {Array.from({
        length: 14
      }, (_, index) => <ellipse key={index} cx="50" cy="22" rx="5.5" ry="20" fill="currentColor" opacity="0.7" transform={`rotate(${index * 360 / 14} 50 50)`} data-id="element-80" data-mp="mhrd9cp1fgd6" />)}
      </g>
      <circle cx="50" cy="50" r="12" fill="currentColor" className={centerClassName} data-id="element-81" data-mp="10dl84rsz857a" />
    </svg>;
}

/** Bell-shaped flowers on an arching stem (gentian / bluebell) — decorative. */
export function Bellflower({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  const bells = [{
    x: 34,
    y: 60,
    r: -18
  }, {
    x: 58,
    y: 92,
    r: 8
  }, {
    x: 40,
    y: 124,
    r: -10
  }];
  return <svg viewBox="0 0 100 200" fill="none" aria-hidden="true" className={className} data-id="element-82" data-mp="1j3kwtdkwnia6">
      <g className={centerClassName} data-id="element-83" data-mp="33ozqsmedf2bi">
        <path d="M70 196C70 140 64 80 44 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" opacity="0.6" data-id="element-84" data-mp="2yov8cqtwjxin" />
        <path d="M68 150C54 146 44 150 36 162C50 166 60 162 68 150Z" fill="currentColor" opacity="0.4" data-id="element-85" data-mp="2um8deh1xpwh8" />
      </g>
      <g className={petalClassName} data-id="element-86" data-mp="2poynl0amvisv">
        {bells.map(bell => <path key={bell.y} transform={`rotate(${bell.r} ${bell.x} ${bell.y})`} d={`M${bell.x - 12} ${bell.y - 16}C${bell.x - 14} ${bell.y + 2} ${bell.x - 18} ${bell.y + 12} ${bell.x - 20} ${bell.y + 18}L${bell.x - 10} ${bell.y + 13}L${bell.x} ${bell.y + 19}L${bell.x + 10} ${bell.y + 13}L${bell.x + 20} ${bell.y + 18}C${bell.x + 18} ${bell.y + 12} ${bell.x + 14} ${bell.y + 2} ${bell.x + 12} ${bell.y - 16}C${bell.x + 6} ${bell.y - 22} ${bell.x - 6} ${bell.y - 22} ${bell.x - 12} ${bell.y - 16}Z`} fill="currentColor" opacity="0.7" data-id="element-87" data-mp="3mdxx0imyeqmn" />)}
      </g>
    </svg>;
}

/** Tall spike of tiny florets (heather / lavender) — decorative. */
export function FlowerSpike({
  className = '',
  petalClassName = '',
  centerClassName = ''
}: FlowerProps) {
  return <svg viewBox="0 0 60 200" fill="none" aria-hidden="true" className={className} data-id="element-88" data-mp="3bwaug6mek4os">
      <path d="M30 196V30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" className={centerClassName} data-id="element-89" data-mp="3gtkknohm8kdo" />
      <g className={petalClassName} data-id="element-90" data-mp="8f2ha02mdt0e">
        {Array.from({
        length: 11
      }, (_, index) => {
        const y = 28 + index * 11;
        const size = 4 + index * 0.35;
        return <g key={index} data-id="element-91" data-mp="3utc1dwzioevd">
              <circle cx={30 - size - 1} cy={y} r={size} fill="currentColor" opacity="0.75" data-id="element-92" data-mp="3us0xzy9zh94s" />
              <circle cx={30 + size + 1} cy={y + 5} r={size} fill="currentColor" opacity="0.6" data-id="element-93" data-mp="3lpulvhi3waa" />
            </g>;
      })}
        <circle cx="30" cy="20" r="4" fill="currentColor" opacity="0.8" data-id="element-94" data-mp="w3v85i4dxao5" />
      </g>
    </svg>;
}

/** Flower essence dropper bottle — decorative. */
export function DropperBottle({
  className = ''
}: DecorProps) {
  return <svg viewBox="0 0 80 160" fill="none" aria-hidden="true" className={className} data-id="element-95" data-mp="11gadov49bb4k">
      <rect x="30" y="6" width="20" height="34" rx="5" fill="currentColor" opacity="0.75" data-id="element-96" data-mp="1f8zywxbyoc0m" />
      <rect x="24" y="36" width="32" height="14" rx="4" fill="currentColor" opacity="0.9" data-id="element-97" data-mp="1a9596x3x9sh5" />
      <path d="M26 50h28c6 0 10 5 10 11v82c0 6-4 11-10 11H26c-6 0-10-5-10-11V61c0-6 4-11 10-11Z" fill="currentColor" opacity="0.32" data-id="element-98" data-mp="mjsz7v0rtgds" />
      <rect x="24" y="86" width="32" height="34" rx="4" fill="currentColor" opacity="0.22" data-id="element-99" data-mp="ikk8z04xw0mz" />
      <path d="M40 130c4-7 7-11 7-15a7 7 0 1 0-14 0c0 4 3 8 7 15Z" fill="currentColor" opacity="0.6" data-id="element-100" data-mp="wjjz6c83onl9" />
    </svg>;
}